import type {
  Venue, HappyHourSchedule, FilterState, DayOfWeek, DealType,
  VenueCategory, PriceTier, UserLocation
} from '../types'
import { JS_DAY_TO_DOW } from '../types'
import { getScheduleStatus, getVenueStatus, REAL_CLOCK, type Clock, type ScheduleStatus, fmtTimeFromMins } from './happeningNow'

export function getTodayDOW(): DayOfWeek {
  return JS_DAY_TO_DOW[new Date().getDay()]
}

export function getCurrentTime(): string {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

export function fmtTime(t: string | null | undefined): string {
  if (!t) return ''
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hr = h % 12 || 12
  return `${hr}${m ? ':' + String(m).padStart(2, '0') : ''} ${ampm}`
}

export function isScheduleActiveNow(
  schedule: HappyHourSchedule,
  clock: Clock = REAL_CLOCK
): boolean {
  const result = getScheduleStatus(schedule, clock)
  return result?.status === 'live_now' || result?.status === 'ends_soon'
}

/**
 * Checks if a venue is physically open for business right now.
 * Uses business_hours from Google Places (stored on the venue).
 *
 * WHY THIS MATTERS:
 * Venues with is_all_day deals (e.g. "$1 wings all day Monday") were showing
 * as "Live Now" even at 8am before the bar opened. This function gates
 * isVenueActiveNow so we check actual opening hours first.
 *
 * FALLBACK: if business_hours is null/empty (most existing venues),
 * returns true — preserving existing behavior for unset venues.
 */
export function isVenueOpenForBusiness(venue: Venue): boolean {
  const periods = venue.business_hours
  if (!periods || periods.length === 0) return true  // no hours set → assume open

  const now  = new Date()
  const day  = now.getDay()   // 0=Sun, 1=Mon ... 6=Sat
  const time = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0')

  // MIDNIGHT SPILLOVER — check yesterday's spans-midnight periods FIRST.
  // A bar open Fri 1pm → Sat 2:30am stores that as ONE period with
  // open.day=5, close.day=6. At 1am Saturday, filtering by open.day===today
  // misses it entirely, so the bar showed as closed while it was open.
  const yesterday = (day + 6) % 7
  const openFromYesterday = periods.some(p =>
    p.open.day === yesterday &&
    p.close != null &&
    p.close.day === day &&
    time < p.close.time
  )
  if (openFromYesterday) return true

  // Find any open period for today
  const todayPeriods = periods.filter(p => p.open.day === day)
  if (todayPeriods.length === 0) return false  // closed today (and no spillover)

  return todayPeriods.some(p => {
    const openTime  = p.open.time
    const closeTime = p.close?.time
    if (!openTime)  return false
    if (!closeTime) return true   // 24-hour venue

    // Spans midnight: close day is different from open day
    if (p.close && p.close.day !== p.open.day) {
      return time >= openTime  // open from openTime until midnight (close is tomorrow)
    }

    return time >= openTime && time < closeTime
  })
}

export function isVenueActiveNow(venue: Venue, clock: Clock = REAL_CLOCK): boolean {
  // Gate 1: is the venue physically open for business?
  if (!isVenueOpenForBusiness(venue)) return false
  // Gate 2: is happy hour currently running?
  return (venue.schedules || []).some(s => isScheduleActiveNow(s, clock))
}

/**
 * getEffectiveVenueStatus — wraps getVenueStatus with a business hours check.
 *
 * Use this everywhere a status badge is shown on a venue card or detail page.
 * Without this, all-day deals show as "Live Now" before the bar opens.
 *
 * getVenueStatus (happeningNow.ts) can't import isVenueOpenForBusiness
 * (filters.ts) without a circular dependency, so the check lives here instead.
 */
/**
 * Minutes-since-midnight when the venue opens today.
 * Returns 0 if no business hours are set (assume open — matches the
 * fallback in isVenueOpenForBusiness), and null if the venue is closed
 * today entirely. Used to compute EFFECTIVE happy hour starts: a deal
 * that starts on paper at 9 AM at a bar that opens at 11 effectively
 * starts at 11 — doors gate deals.
 */
export function getBusinessOpenMins(venue: Venue, day: number): number | null {
  const periods = venue.business_hours
  if (!periods || periods.length === 0) return 0
  const todays = periods.filter(p => p.open.day === day && p.open.time)
  if (todays.length === 0) return null  // closed today
  let earliest = Infinity
  for (const p of todays) {
    const mins = parseInt(p.open.time.slice(0, 2), 10) * 60 + parseInt(p.open.time.slice(2), 10)
    if (!isNaN(mins) && mins < earliest) earliest = mins
  }
  return earliest === Infinity ? null : earliest
}


/**
 * Rewrite a schedule status for a venue whose DOORS aren't open yet.
 * Every downgrade site previously did `{ ...best, status: 'later_today' }`,
 * which swapped the badge COLOR but kept the badge TEXT — shipping
 * contradictory "Live Now" chips in later-today blue. This is the single
 * source of truth for the downgrade: text, status, and timing all agree,
 * and the chip says something useful ("Opens at 5 PM") because doors —
 * not the paper schedule — are what gate the deal.
 */
export function downgradeForClosedDoors(venue: Venue, best: ScheduleStatus): ScheduleStatus {
  const d = new Date()
  const nowMins = d.getHours() * 60 + d.getMinutes()
  const openMins = getBusinessOpenMins(venue, d.getDay())

  if (openMins === null) {
    // Closed all day — the deal can't happen today at all
    return { ...best, status: 'not_today', minutesUntil: null, minutesRemaining: null,
             label: 'Not today', badge: 'Not today' }
  }
  if (openMins > nowMins) {
    const until = openMins - nowMins
    const fmtDur = until >= 60 ? `${Math.floor(until / 60)}h ${until % 60}m` : `${until}m`
    if (until <= 30) {
      return { ...best, status: 'starts_soon', minutesUntil: until, minutesRemaining: null,
               label: `Opens in ${fmtDur}`, badge: `Opens in ${fmtDur}` }
    }
    return { ...best, status: 'later_today', minutesUntil: until, minutesRemaining: null,
             label: `Opens at ${fmtTimeFromMins(openMins)}`, badge: `Opens at ${fmtTimeFromMins(openMins)}` }
  }
  // Doors nominally open but between split-hours periods, or spillover edge —
  // can't name a time confidently, so say the honest generic thing
  return { ...best, status: 'later_today', minutesUntil: null, minutesRemaining: null,
           label: 'Later today', badge: 'Later today' }
}

export function getEffectiveVenueStatus(
  venue: Venue,
  clock: Clock = REAL_CLOCK
): ReturnType<typeof getVenueStatus> {
  const status = getVenueStatus(venue, clock)
  // If venue isn't open for business, downgrade live → later_today
  if (
    !isVenueOpenForBusiness(venue) &&
    (status.status === 'live_now' || status.status === 'ends_soon')
  ) {
    return downgradeForClosedDoors(venue, status)
  }
  return status
}

export function getActiveScheduleNow(
  venue: Venue,
  clock: Clock = REAL_CLOCK
): HappyHourSchedule | null {
  return (venue.schedules || []).find(s => isScheduleActiveNow(s, clock)) ?? null
}

export function getSchedulesForDay(venue: Venue, day: DayOfWeek): HappyHourSchedule[] {
  return (venue.schedules || []).filter(s => s.days.includes(day))
}

export function getVenueActiveDays(venue: Venue): DayOfWeek[] {
  const days = new Set<DayOfWeek>()
  ;(venue.schedules || []).forEach(s => s.days.forEach(d => days.add(d as DayOfWeek)))
  return Array.from(days)
}

export function distanceMiles(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 3958.8
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function fmtDistance(miles: number): string {
  if (miles < 0.05) return '< 0.1 mi'
  return `${miles.toFixed(1)} mi`
}

export function filterVenues(
  venues: Venue[],
  filters: FilterState,
  userLocation?: UserLocation | null,
  maxDistanceMiles = 25
): Venue[] {
  return venues.filter(venue => {
    if (filters.city && venue.city !== filters.city) return false
    if (filters.openNow && !isVenueActiveNow(venue)) return false
    if (filters.dogFriendly && !venue.dog_friendly) return false  // ← no more as any

    if (filters.days.size > 0) {
      const venueDays = getVenueActiveDays(venue)
      const hasMatch = [...filters.days].some(d => venueDays.includes(d))
      if (!hasMatch) return false
    }

    if (filters.timeWindow) {
      const { start, end } = filters.timeWindow
      const hasOverlap = (venue.schedules || []).some(s => {
        if (s.is_all_day) return true
        return !(s.end_time <= start || s.start_time >= end)
      })
      if (!hasOverlap) return false
    }

    if (filters.dealTypes.size > 0) {
      const dealTypeKeywords: Record<string, string[]> = {
        beer:     ['beer', 'draft', 'brew', 'lager', 'ale', 'pint'],
        wine:     ['wine', 'vino', 'champagne', 'prosecco', 'bubbly'],
        cocktail: ['cocktail', 'well', 'spirit', 'margarita', 'martini', 'mixed'],
        liquor:   ['liquor', 'shot', 'whiskey', 'bourbon', 'vodka', 'tequila', 'gin', 'rum'],
        food:     ['food', 'app', 'apps', 'appetizer', 'bite', 'wing', 'burger', 'taco', 'pizza', 'snack', 'half-off', 'half off', 'menu'],
        general:  [],
      }
      // LEADING word boundary only. Keywords must START a word, but may
      // extend ('draft' matches 'drafts', 'well' matches 'wells').
      // A trailing \b — the first version of this fix — broke plural
      // matching: \bdraft\b failed on "$3 drafts", silently dropping
      // venues from the Beer/Cocktail/Liquor chips. The leading boundary
      // alone still blocks the original bug ('app' cannot match "happy"
      // because its 'a' is mid-word).
      const kwMatch = (text: string, kw: string) => {
        const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        return new RegExp(`\\b${escaped}`, 'i').test(text)
      }
      const hasType = (venue.schedules || []).some(s => {
        if (s.deals.some(d => filters.dealTypes.has(d.type as DealType))) return true
        const text = (s.deal_text || '').toLowerCase()
        return [...filters.dealTypes].some(type => {
          if (type === 'general') return true
          return (dealTypeKeywords[type] || []).some(kw => kwMatch(text, kw))
        })
      })
      if (!hasType) return false
    }

    if (filters.categories.size > 0) {
      const hasCategory = venue.categories.some(c =>
        filters.categories.has(c as VenueCategory)
      )
      if (!hasCategory) return false
    }

    if (filters.neighborhoods.size > 0) {
      if (!filters.neighborhoods.has(venue.neighborhood)) return false
    }

    if (filters.priceTiers.size > 0) {
      if (!venue.price_tier || !filters.priceTiers.has(venue.price_tier as PriceTier)) return false
    }

    if (userLocation && venue.latitude && venue.longitude) {
      const miles = distanceMiles(
        userLocation.lat, userLocation.lng,
        venue.latitude, venue.longitude
      )
      if (miles > maxDistanceMiles) return false
    }

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase()
      const searchable = [
        venue.name,
        venue.neighborhood,
        venue.address ?? '',
        ...(venue.schedules || []).map(s => s.deal_text),
        ...(venue.schedules || []).flatMap(s => s.deals.map(d => d.description)),
      ].join(' ').toLowerCase()
      if (!searchable.includes(q)) return false
    }

    return true
  })
}

export function getNeighborhoods(venues: Venue[]): string[] {
  return [...new Set(venues.map(v => v.neighborhood).filter(Boolean))].sort()
}

export function getMinDealPrice(venue: Venue): number | null {
  const prices = (venue.schedules || [])
    .flatMap(s => s.deals)
    .map(d => d.price)
    .filter((p): p is number => p != null)
  return prices.length ? Math.min(...prices) : null
}

export function isRecentlyVerified(venue: Venue): boolean {
  if (!venue.last_verified_at) return false
  const days = (Date.now() - new Date(venue.last_verified_at).getTime()) / 86_400_000
  return days <= 30
}

export function verifiedAgo(venue: Venue): string | null {
  if (!venue.last_verified_at) return null
  const days = Math.floor(
    (Date.now() - new Date(venue.last_verified_at).getTime()) / 86_400_000
  )
  if (days === 0) return 'Verified today'
  if (days === 1) return 'Verified yesterday'
  if (days < 30) return `Verified ${days}d ago`
  if (days < 365) return `Verified ${Math.floor(days / 30)}mo ago`
  return 'Not recently verified'
}
